import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../api/store";
import { ageBracketAndBenefitAmountState, ForBorrowerAge, SetBorrowerAgeAmountFromApiPayload, SetBorrowerAgeFromApiPayload, UpdateBorrowerAgePayload, UpdateSingleRateCheckedPayload } from "./age-bracket-and-benefit-amount.state";
import { initialRowsFor6670, initialRowsFor7175, initialRowsFor7680, optionForBorrowerAge, profileAgeRate, remainingAgeRate1865 } from "./age-bracket-and-benefit-amount.initial_state";

export type UpdateBorrowerAgePayloadtype = 'rAge' | 'fAge'
export type ratesTable =  "6670" | "7175";

const initialState: ageBracketAndBenefitAmountState = {
    remainingAgeRate: remainingAgeRate1865,
    profileAgeRate: profileAgeRate,
    borrowerAgeOptions: optionForBorrowerAge,
    singleRateTableFor6670: initialRowsFor6670,
    singleRateTableFor7175: initialRowsFor7175,
    singleRateTableFor7680: initialRowsFor7680,
    selected6670: null,
    selected7175: null,
    selected7680: null,
};

const ageBracketAndBenefitAmountSlice = createSlice({
    name: "ageBracketAndBenefitAmountSlice",
    initialState,
    reducers: {
        setBorrowerAgeIsShow: (state, action: PayloadAction<{ id: number, isShow: boolean}>) => {
            const { id, isShow } = action.payload;
            const option = state.borrowerAgeOptions.find(item => item.id === id);

            if (option) {
                option.isShow = isShow
            }
        },
        setBorrowerAgeValue: (state, action: PayloadAction<UpdateBorrowerAgePayload>) => {
            const { id, value, type } = action.payload;
            if (type === 'fAge') {
                const option = state.borrowerAgeOptions.find(item => item.id === id);

                if (option) {
                    option.value = value;

                    if (!value) {
                        option.amount = null;
                    }
                }
                return
            } else if (type === 'rAge') {
                const optionRemainingAge = state.remainingAgeRate.find(item => item.id === id);

                if (optionRemainingAge) {
                    optionRemainingAge.value = value;

                    if (!value) {
                        optionRemainingAge.amount = null;
                    }
                }
                return
            }
        },

        setSingleRateChecked: (state, action: PayloadAction<UpdateSingleRateCheckedPayload>) => {
            const { index, table } = action.payload;

            const tableKey = table === "6670" ? "singleRateTableFor6670" : "singleRateTableFor7175";

            state[tableKey] = state[tableKey].map((row, i) => ({
                ...row,
                checked: i === index,
            }));
        },

        setBorrowerAgeFromAPI: (state, action: PayloadAction<{value?: SetBorrowerAgeFromApiPayload, fAmount?: SetBorrowerAgeAmountFromApiPayload,  type: 'check' | 'amount'}>) => {
            const { value, fAmount, type } = action.payload;

            state.borrowerAgeOptions = state.borrowerAgeOptions.map((option) => {

                switch (option.id) {

                    case 3:
                        return {
                            ...option,
                            ...(type === 'check' && value
                                ? { value: value.borrower_age_66_70 }
                                : {}
                            ),
                            ...(type === 'amount' && fAmount
                                ? { amount: fAmount.borrower_amount_66_70 }
                                : {}
                            )
                        };

                    case 4:
                        return {
                            ...option,
                            ...(type === 'check' && value
                                ? { value: value.borrower_age_71_75 }
                                : {}
                            ),
                            ...(type === 'amount' && fAmount
                                ? { amount: fAmount.borrower_amount_71_75 }
                                : {}
                            )
                        };

                    case 5:
                        return {
                            ...option,
                            ...(type === 'check' && value
                                ? { value: value.borrower_age_76_80 }
                                : {}
                            ),
                            ...(type === 'amount' && fAmount
                                ? { amount: fAmount.borrower_amount_76_80 }
                                : {}
                            )
                        };

                    default:
                        return option;
                }

            });
        },

        setBorrowerAgeAmount: (state, action: PayloadAction<{ id: number; amount: number | null }>) => {
            const { id, amount } = action.payload;

            const option = state.borrowerAgeOptions.find(item => item.id === id);

            if (option) {
                option.amount = amount;
            }
        },
        addBorrowerAgeOptionData: (state, action: PayloadAction<ForBorrowerAge>) => {
            // state.borrowerAgeOptions.push(action.payload);
            const exists = state.borrowerAgeOptions.some(
                item => item.id === action.payload.id
            );

            if (!exists) {
                state.borrowerAgeOptions.push(action.payload);
            }
        },
        addBorrowerAgeOption: (state, action: PayloadAction<{id: number; label: string, typeOfAge: string, amount?: number | null, type: 'rm' | 'ap'}>) => {
            const { id, label, type, amount, typeOfAge } = action.payload;
            
            if (type === "rm") {
                const item = state.remainingAgeRate.find(rate => rate.id === id);
                if (item) {
                    item.label = label;
                    item.typeOfAge = typeOfAge;
                    item.isShow = label.trim() !== "";
                    
                    // Only update amount if it was passed
                    if (amount !== undefined) {
                        item.amount = amount;
                    }
                }
            }
            if(type === 'ap') {
                state.profileAgeRate.label = label
            }
            
        },
        updateBorrowerAgeAmount: ( state, action: PayloadAction<{ id: number; amount: number | null; }>) => {
            const { id, amount } = action.payload;

            const item = state.remainingAgeRate.find(rate => rate.id === id);

            if (item) {
                item.amount = amount;
            }
        },
        updateBorrowerAgeValue: ( state, action: PayloadAction<{ id: number; value: boolean; amount?: number | null; }> ) => {
            const { id, value, amount } = action.payload;

            const item = state.remainingAgeRate.find(
                (rate) => rate.id === id
            );

            if (item) {
                item.value = value;
                // Only update amount if a value was provided
                if (amount !== undefined) {
                    item.amount = amount;
                }
            }
        },
        setRemainingAgeAmount: (state,action: PayloadAction<{id: number; value: number | null, type: 'rm' | 'ap'}>) => {
            const { value, type, id } = action.payload;
            if (type === "rm") {
                const item = state.remainingAgeRate.find(rate => rate.id === id);
                if (item) {
                    item.amount = value;
                }
            }
            if(type === 'ap') {
                state.profileAgeRate.amount = value
            }
        },
        setUpdateFromAPI: (state,action: PayloadAction<{value: { label: string, amount: number }[], type: 'rm' | 'ap'}>) => {
            const { value, type } = action.payload;
            // const { amount, label } = value
            // if(type === 'rm') {
            //     state.remainingAgeRate.amount = amount
            //     state.remainingAgeRate.label = label
            // }
            if (type === "rm") {
                state.remainingAgeRate = state.remainingAgeRate.map((item, index) => ({
                    ...item,
                    label: value[index]?.label ?? "",
                    amount: value[index]?.amount ?? 0,
                }));
            }
            if(type === 'ap') {
                const first = value[0];
                state.profileAgeRate.amount = first.amount
                state.profileAgeRate.label = first.label
            }
        },
        setRemainingAgeValue: (state, action: PayloadAction<boolean>) => {
            // state.remainingAgeRate.value = action.payload;
        },
        resetRemainingAge: (state) => {
            state.remainingAgeRate = [
                { id: 1, label: "", amount: 0, value: true, isDefault: false },
                { id: 2, label: "", amount: 0, value: true, isDefault: false },
                { id: 3, label: "", amount: 0, value: true, isDefault: false },
            ];
        },
        resetProfileAge: (state) => {
            state.profileAgeRate = { id: 2, label: "", amount: 0, value: false, isDefault: false }
        },
        // resetBorrowerAge: () => initialState,
        resetBorrowerAge: (state) => {
            // state.borrowerAgeOptions = optionForBorrowerAge;
            state.singleRateTableFor6670 = initialRowsFor6670;
            state.singleRateTableFor7175 = initialRowsFor7175;
            state.singleRateTableFor7680 = initialRowsFor7680;

            state.selected6670 = null;
            state.selected7175 = null;
            state.selected7680 = null;
        },
        resetBorrowerAgeOptions: (state) => {
            state.borrowerAgeOptions = optionForBorrowerAge;
        }
    },
});

export const { 
    setBorrowerAgeIsShow,
    setBorrowerAgeValue,
    setSingleRateChecked,
    resetBorrowerAge,
    setBorrowerAgeAmount,
    addBorrowerAgeOption,
    updateBorrowerAgeAmount,
    updateBorrowerAgeValue,
    setRemainingAgeAmount,
    resetRemainingAge,
    setRemainingAgeValue,
    setBorrowerAgeFromAPI,
    addBorrowerAgeOptionData,
    resetBorrowerAgeOptions,
    setUpdateFromAPI,
    resetProfileAge,
} = ageBracketAndBenefitAmountSlice.actions;

// remainingAgeRate
// export const selectRemainingAgeRateState = (state: RootState) => state.ageBracketAndBenefitAmountSlice.remainingAgeRate;
export const selectRemainingAgeRateState = (state: RootState) => state.ageBracketAndBenefitAmountSlice.remainingAgeRate;
// export const visibleRemainingAge = (state: RootState) => selectRemainingAgeRateState(state).filter(item => item.isShow)
export const visibleRemainingAge = createSelector([selectRemainingAgeRateState], (remainingAgeRate) => remainingAgeRate.filter(item => item.isShow));
// profileAgeRate
export const selectAgeProfileRateState = (state: RootState) => state.ageBracketAndBenefitAmountSlice.profileAgeRate;
export const selectAgeProfileRateAmountState = (state: RootState) => state.ageBracketAndBenefitAmountSlice.profileAgeRate.amount;
// borrowerAgeOptions
export const selectBorrowerAgeSlice = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions.map(option => option.value);
export const borrowerAgeValuesState = createSelector(
    [(state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions],
    (options) => options.map(option => option.value)
);
export const borrowerAgeOptionsState = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions;
export const selected6669State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[0].value;
export const selected6669Amount = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[0].amount;
export const selected6670State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[1].value;
export const selected6670Amount = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[1].amount;
export const selected7175State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[2].value;
export const selected7175Amount = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[2].amount;
export const selected7680State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[3].value;
export const selected7680Amount = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[3].amount;
// export const selected1865Amount = (state: RootState) => state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions[0].amount;
// singleRateTableFor6670
export const singleRateTableFor6670State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.singleRateTableFor6670;
// singleRateTableFor7175
export const singleRateTableFor7175State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.singleRateTableFor7175;
// singleRateTableFor7680
export const singleRateTable7680State = (state: RootState) => state.ageBracketAndBenefitAmountSlice.singleRateTableFor7680;

export default ageBracketAndBenefitAmountSlice.reducer;
